import React, {
  useRef,
  useCallback,
  useEffect,
  useState,
  useMemo,
} from 'react';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import clsx from 'clsx';
import { createOrganizationValidationSchema } from '@/utils/validations';
import { ModalProps } from '@mui/base/Modal';
import { Modal } from '@/components';
import {
  useGetRegionsQuery,
  useGetAdminCandidatesQuery,
} from '@/services/OrganizationManagementService';
import DMOAdminFields from '../user-management/DMOAdminFields';
import { IOrganizationData, AdminOption } from '@/types/organization.types';
import LogoUpload from './LogoUpload';
import { useLanguage, I18N_LANGUAGES } from '@/i18n';
import { useIntl } from 'react-intl';
import OrganizationPreviewModal from './OrganizationPreviewModal';
import { KeenIcon } from '@/components';

export interface OrganizationFormModalProps extends Omit<
  ModalProps,
  'children' | 'onSubmit'
> {
  isError?: boolean;
  mode: 'view' | 'edit' | 'create';
  formData: Partial<IOrganizationData>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<IOrganizationData>>>;
  errors: { [key: string]: string };
  setErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  onSubmit: (data: Partial<IOrganizationData>) => Promise<void>;
  isSubmitting?: boolean;
}

const OrganizationFormModal = ({
  mode,
  open,
  onClose,
  formData,
  setFormData,
  errors,
  setErrors,
  onSubmit,
  isSubmitting,
  isError,
  className,
}: OrganizationFormModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const adminDropdownRef = useRef<HTMLDivElement>(null);
  const organisationNameRef = useRef<HTMLInputElement>(null);
  const [isAdminDropdownOpen, setIsAdminDropdownOpen] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const { data: adminUserListing } = useGetAdminCandidatesQuery(
    {},
    {
      skip: !open,
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: regionsData, isLoading: isRegionsLoading } = useGetRegionsQuery(
    {
      page: 1,
      limit: 100,
      orphaned: true,
    },
    {
      skip: !open,
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: allRegionsData } = useGetRegionsQuery(
    {
      page: 1,
      limit: 100,
    },
    {
      skip: !open,
      refetchOnMountOrArgChange: true,
    }
  );
  const intl = useIntl();
  const { currentLanguage, changeLanguage } = useLanguage();

  const regionOptions = useMemo(() => {
    const orphanedOptions: { value: string; label: string }[] = Array.isArray(
      regionsData?.data
    )
      ? regionsData.data.map((region: { id: string; name: string }) => ({
          value: region.id,
          label: region.name,
        }))
      : [];

    const selectedAdminId = formData.user?.id;
    const adminFromList = Array.isArray(adminUserListing?.data)
      ? adminUserListing.data.find((a: AdminOption) => a.id === selectedAdminId)
      : undefined;
    const adminRegionSource: { id: string; name: string }[] = Array.isArray(
      formData.user?.regionDmo
    )
      ? (formData.user!.regionDmo as { id: string; name: string }[])
      : Array.isArray(adminFromList?.regionDmo)
        ? (adminFromList!.regionDmo as { id: string; name: string }[])
        : [];
    const adminAssignedOptions: { value: string; label: string }[] =
      adminRegionSource.map((r: { id: string; name: string }) => ({
        value: r.id,
        label: r.name,
      }));

    const orphanedMap = new Map<string, string>(
      orphanedOptions.map(
        (o: { value: string; label: string }) => [o.value, o.label] as const
      )
    );
    const adminMap = new Map<string, string>(
      adminAssignedOptions.map(
        (o: { value: string; label: string }) => [o.value, o.label] as const
      )
    );
    const allRegionsMap = new Map<string, string>(
      Array.isArray(allRegionsData?.data)
        ? allRegionsData.data.map(
            (r: { id: string; name: string }) => [r.id, r.name] as const
          )
        : []
    );
    const selectedOptions: { value: string; label: string }[] = (
      formData.linked_regions || []
    ).map((id: string) => ({
      value: id,
      label:
        adminMap.get(id) || orphanedMap.get(id) || allRegionsMap.get(id) || id,
    }));

    const mergedMap = new Map<string, { value: string; label: string }>();
    [...adminAssignedOptions, ...orphanedOptions, ...selectedOptions].forEach(
      (opt: { value: string; label: string }) => {
        if (!mergedMap.has(opt.value)) mergedMap.set(opt.value, opt);
      }
    );
    return Array.from(mergedMap.values());
  }, [
    regionsData,
    formData.user,
    formData.linked_regions,
    adminUserListing,
    allRegionsData,
  ]);

  const organizationValidationSchema = useMemo(
    () => createOrganizationValidationSchema(mode, intl),
    [mode, intl]
  );

  const validateField = useCallback(
    async (name: keyof IOrganizationData | 'user_id', value: unknown) => {
      try {
        await organizationValidationSchema.validateAt(name, { [name]: value });
        setErrors((prev) => ({ ...prev, [name]: '' }));
      } catch (err) {
        console.error('Validation error:', err);
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => ({ ...prev, [name]: err.message }));
        }
      }
    },
    [setErrors, organizationValidationSchema]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        adminDropdownRef.current &&
        !adminDropdownRef.current.contains(event.target as Node)
      ) {
        setIsAdminDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Autofocus the Organization Name field when modal opens (create/edit)
  useEffect(() => {
    if (open && mode !== 'view') {
      const t = setTimeout(() => organisationNameRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [open, mode]);

  const handleAdminSelect = (admin: AdminOption) => {
    const linkedRegionIds = admin.regionDmo?.map((region) => region.id) || [];
    setFormData((prev) => ({
      ...prev,
      user: admin,
      user_id: admin.id,
      linked_regions: linkedRegionIds,
    }));
    validateField('user_id', admin.id);
    setIsAdminDropdownOpen(false);
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    const snakeCaseName = name.replace(
      /[A-Z]/g,
      (letter) => `_${letter.toLowerCase()}`
    );
    setFormData((prev) => ({ ...prev, [snakeCaseName]: value }));
  };

  const handleLinkedRegionsChange = (
    selected: readonly { value: string; label: string }[]
  ) => {
    const selectedValues = selected.map((option) => option.value);
    setFormData((prev) => ({
      ...prev,
      linked_regions: selectedValues,
    }));
  };

  const handleLogoChange = (file: File | string | null) => {
    setFormData((prev) => ({
      ...prev,
      logo: file,
    }));
  };

  const handleBlur = (name: string) => {
    const snakeCaseName = name.replace(
      /[A-Z]/g,
      (letter) => `_${letter.toLowerCase()}`
    );
    validateField(
      snakeCaseName as keyof IOrganizationData,
      formData[snakeCaseName as keyof IOrganizationData]
    );
  };

  const handleTranslationsGenerated = useCallback(
    (
      newTranslations: Record<
        string,
        Partial<{ organisation_name: string; description: string }>
      >
    ) => {
      const sanitizedTranslations = Object.fromEntries(
        Object.entries(newTranslations).map(([lang, translation]) => [
          lang,
          {
            description: translation.description,
          },
        ])
      );
      setFormData((prev) => ({
        ...prev,
        translations: sanitizedTranslations,
      }));
    },
    [setFormData]
  );

  const handleSubmit = async () => {
    try {
      await organizationValidationSchema.validate(formData, {
        abortEarly: false,
      });
      const { translations, ...restFormData } = formData;
      const submissionData: Partial<IOrganizationData> = {
        ...restFormData,
        translations: translations ? JSON.stringify(translations) : undefined,
        language: currentLanguage.code,
      };
      await onSubmit(submissionData);
    } catch (err) {
      console.error('Caught error in handleSubmit:', err);
      if (err instanceof Yup.ValidationError) {
        const validationErrors: { [key: string]: string } = {};
        if (Array.isArray(err.inner) && err.inner.length > 0) {
          err.inner.forEach((error: Yup.ValidationError) => {
            if (error.path) validationErrors[error.path] = error.message;
          });
        }
        setErrors(validationErrors);
        enqueueSnackbar(intl.formatMessage({ id: 'ORGANIZATION.ERROR.FORM' }), {
          variant: 'error',
        });
      }
    }
  };

  const canShowPreview = !!formData.organisation_name && !!formData.description;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        className={clsx('!flex items-center justify-center', className)}
        data-testid="organization-form-modal"
        aria-labelledby="organization-form-modal-title"
      >
        <div className="bg-gray-100 w-full max-w-md mx-4 rounded-xl sm:rounded-lg shadow-2xl sm:shadow-lg flex flex-col p-4 sm:p-6 transform transition-all duration-200 max-h-[97dvh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h1
              id="organization-form-modal-title"
              className="text-lg font-medium text-gray-900"
              data-testid="modal-title"
            >
              {mode === 'create'
                ? intl.formatMessage({ id: 'ORGANIZATION.TITLE.CREATE' })
                : mode === 'edit'
                  ? intl.formatMessage({ id: 'ORGANIZATION.TITLE.EDIT' })
                  : intl.formatMessage({ id: 'ORGANIZATION.TITLE.VIEW' })}
            </h1>
            <div className="relative">
              <label htmlFor="language-select" className="sr-only">
                {intl.formatMessage({ id: 'USER.MENU.LANGUAGE' })}
              </label>
              <select
                id="language-select"
                className="select select-bordered pl-8 pr-8 h-12 md:h-10 leading-normal"
                value={currentLanguage.code}
                onChange={(e) => {
                  const selectedLang = I18N_LANGUAGES.find(
                    (lang) => lang.code === e.target.value
                  );
                  if (selectedLang) {
                    changeLanguage(selectedLang);
                  }
                }}
                aria-label={intl.formatMessage({ id: 'USER.MENU.LANGUAGE' })}
              >
                {I18N_LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.label}
                  </option>
                ))}
              </select>
              <img
                src="/media/flags/globe.svg"
                alt="Globe"
                className="absolute dark:brightness-0 contrast:brightness-0 dark:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
                aria-hidden="true"
              />
            </div>
          </div>
          {isError && (mode === 'edit' || mode === 'view') ? (
            <div
              className="text-red-500 text-center py-6"
              data-testid="organization-detail-error"
            >
              {intl.formatMessage({ id: 'ORGANIZATION.ERROR.LOAD' })}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto max-h-[70vh] pr-0 md:pr-3 custom-scrollbar">
                <div className="flex flex-col gap-4">
                  <div>
                    <label
                      htmlFor="organisationName-input"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {intl.formatMessage({ id: 'ORGANIZATION.FORM.NAME' })}
                    </label>
                    <input
                      id="organisationName-input"
                      type="text"
                      name="organisation_name"
                      autoComplete="organization"
                      value={formData.organisation_name || ''}
                      ref={organisationNameRef}
                      disabled={isSubmitting || mode === 'view'}
                      onChange={handleInputChange}
                      onKeyDown={(e) => {
                        if (
                          e.key === ' ' &&
                          (e.target as HTMLInputElement).value === ''
                        ) {
                          e.preventDefault();
                        }
                      }}
                      onBlur={() => handleBlur('organisation_name')}
                      className={clsx(
                        'input input-bordered w-full h-12 md:h-10 border-gray-300 focus:ring-primary focus:border-primary',
                        errors.organisation_name &&
                          'border-red-500 focus:ring-red-500',
                        mode === 'view' && 'bg-gray-100 cursor-not-allowed'
                      )}
                      aria-invalid={!!errors.organisation_name}
                      aria-describedby={
                        errors.organisation_name
                          ? 'organisation_name-error'
                          : undefined
                      }
                      placeholder={intl.formatMessage({
                        id: 'ORGANIZATION.FORM.NAME.PLACEHOLDER',
                      })}
                      data-testid="organisation-name-input"
                    />
                    {errors.organisation_name && (
                      <p
                        id="organisation_name-error"
                        className="text-red-500 text-sm mt-1"
                      >
                        {errors.organisation_name}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      htmlFor="organisationType-input"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {intl.formatMessage({ id: 'ORGANIZATION.FORM.TYPE' })}
                    </label>
                    <select
                      id="organisationType-input"
                      name="organisation_type"
                      value={formData.organisation_type || ''}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('organisation_type')}
                      disabled={isSubmitting || mode === 'view'}
                      className={clsx(
                        'select select-sm w-full h-12 md:h-10 border-gray-300 focus:ring-primary focus:border-primary',
                        errors.organisation_type &&
                          'border-red-500 focus:ring-red-500',
                        mode === 'view' && 'bg-gray-100 cursor-not-allowed'
                      )}
                      aria-invalid={!!errors.organisation_type}
                      aria-describedby={
                        errors.organisation_type
                          ? 'organisation_type-error'
                          : undefined
                      }
                    >
                      <option value="" disabled>
                        {intl.formatMessage({
                          id: 'ORGANIZATION.FORM.TYPE.SELECT',
                        })}
                      </option>
                      <option value="DMO">DMO</option>
                      <option value="Regional Tourism Board">
                        Regional Tourism Board
                      </option>
                      <option value="City Marketing">City Marketing</option>
                    </select>
                    {errors.organisation_type && (
                      <p
                        id="organisation_type-error"
                        className="text-red-500 text-sm mt-1"
                      >
                        {errors.organisation_type}
                      </p>
                    )}
                  </div>

                  <div>
                    <label
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {intl.formatMessage({ id: 'ORGANIZATION.FORM.LOGO' })}
                    </label>
                    <LogoUpload
                      value={formData.logo as string | File | null}
                      onChange={handleLogoChange}
                      error={errors.logo}
                      disabled={mode === 'view'}
                      inputId="logo-upload"
                      data-testid="organization-logo-upload"
                      inputTestId="organization-logo-input"
                      aria-label="Logo upload"
                    />
                  </div>

                  <div>
                    <label
                      id="admin-label"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      {intl.formatMessage({ id: 'ORGANIZATION.FORM.ADMIN' })}
                    </label>
                    <div className="relative" ref={adminDropdownRef}>
                      <div
                        id="admin-combobox"
                        data-testid="admin-input"
                        className={clsx(
                          'input input-bordered w-full h-12 md:h-10 flex items-center justify-between cursor-pointer',
                          isAdminDropdownOpen &&
                            mode !== 'view' &&
                            'border-primary',
                          errors.user_id && 'border-red-500',
                          mode === 'view' && 'bg-gray-100 cursor-not-allowed'
                        )}
                        onClick={() =>
                          mode !== 'view' &&
                          setIsAdminDropdownOpen((prev) => !prev)
                        }
                        onKeyDown={(e) => {
                          if (mode === 'view') return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setIsAdminDropdownOpen((prev) => !prev);
                          } else if (
                            e.key === 'Escape' &&
                            isAdminDropdownOpen
                          ) {
                            setIsAdminDropdownOpen(false);
                          } else if (
                            e.key === 'ArrowDown' &&
                            !isAdminDropdownOpen
                          ) {
                            e.preventDefault();
                            setIsAdminDropdownOpen(true);
                          }
                        }}
                        tabIndex={0}
                        aria-label={intl.formatMessage({
                          id: 'ORGANIZATION.FORM.ADMIN.SELECT',
                        })}
                        aria-invalid={!!errors.user_id}
                        aria-expanded={isAdminDropdownOpen}
                        aria-haspopup="listbox"
                        aria-controls={
                          isAdminDropdownOpen && mode !== 'view'
                            ? 'admin-listbox'
                            : undefined
                        }
                        aria-activedescendant={
                          isAdminDropdownOpen && formData.user?.id
                            ? `admin-option-${formData.user.id}`
                            : undefined
                        }
                        aria-labelledby="admin-label"
                        role="combobox"
                      >
                        <span
                          className={
                            formData.user?.email
                              ? 'text-gray-900 dark:text-gray-900'
                              : 'text-gray-500 dark:text-gray-600'
                          }
                        >
                          {formData.user?.email ||
                            intl.formatMessage({
                              id: 'ORGANIZATION.FORM.ADMIN.SELECT',
                            })}
                          {formData.user?.name
                            ? ` (${formData.user.name})`
                            : ''}
                        </span>
                        <svg
                          className={clsx(
                            'w-4 h-4 text-gray-400',
                            isAdminDropdownOpen && 'transform rotate-180'
                          )}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                      {isAdminDropdownOpen && mode !== 'view' && (
                        <div
                          id="admin-listbox"
                          role="listbox"
                          aria-labelledby="admin-label"
                          className="absolute z-50 w-full bottom-[110%] bg-white border border-gray-300 rounded-md shadow-lg max-h-[170px] overflow-y-auto contrast:bg-black contrast:text-white dark:bg-black dark:text-white"
                        >
                          {Array.isArray(adminUserListing?.data) &&
                          adminUserListing.data.length > 0 ? (
                            adminUserListing.data.map(
                              (admin: AdminOption, index: number) => (
                                <div
                                  id={`admin-option-${admin.id}`}
                                  key={admin.id}
                                  data-testid={`admin-option-${admin.id}`}
                                  className={clsx(
                                    'px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 focus:bg-gray-100 focus:outline-none',
                                    formData.user?.id === admin.id
                                  )}
                                  onClick={() => handleAdminSelect(admin)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      handleAdminSelect(admin);
                                    } else if (e.key === 'ArrowDown') {
                                      e.preventDefault();
                                      const adminOptions =
                                        adminUserListing.data as unknown as AdminOption[];
                                      const nextIndex =
                                        (index + 1) % adminOptions.length;
                                      const nextElement = e.currentTarget
                                        .parentElement?.children[
                                        nextIndex
                                      ] as HTMLElement;
                                      nextElement?.focus();
                                    } else if (e.key === 'ArrowUp') {
                                      e.preventDefault();
                                      const adminOptions =
                                        adminUserListing.data as unknown as AdminOption[];
                                      const prevIndex =
                                        index === 0
                                          ? adminOptions.length - 1
                                          : index - 1;
                                      const prevElement = e.currentTarget
                                        .parentElement?.children[
                                        prevIndex
                                      ] as HTMLElement;
                                      prevElement?.focus();
                                    } else if (e.key === 'Escape') {
                                      setIsAdminDropdownOpen(false);
                                      // Focus back to the combobox
                                      const combobox = e.currentTarget
                                        .closest('.relative')
                                        ?.querySelector(
                                          '[role="combobox"]'
                                        ) as HTMLElement;
                                      combobox?.focus();
                                    }
                                  }}
                                  tabIndex={0}
                                  role="option"
                                  aria-selected={formData.user?.id === admin.id}
                                >
                                  {admin.email}
                                  {admin.name ? ` (${admin.name})` : ''}
                                </div>
                              )
                            )
                          ) : (
                            <div
                              className="px-3 py-2 text-sm text-gray-500"
                              data-testid="no-admins"
                            >
                              {intl.formatMessage({
                                id: 'ORGANIZATION.FORM.ADMIN.NONE',
                              })}
                            </div>
                          )}
                        </div>
                      )}
                      {errors.user_id && (
                        <p
                          id="user_id-error"
                          className="text-red-500 text-sm mt-1"
                          data-testid="user_id-error"
                        >
                          {errors.user_id}
                        </p>
                      )}
                    </div>
                  </div>

                  <DMOAdminFields
                    formData={formData}
                    errors={errors}
                    isCreating={isSubmitting || false}
                    isRegionsLoading={isRegionsLoading}
                    regionOptions={regionOptions}
                    handleInputChange={handleInputChange}
                    handleLinkedRegionsChange={handleLinkedRegionsChange}
                    handleBlur={handleBlur}
                    mode={mode}
                    intl={intl}
                    data-testid="linked-regions-input"
                    showOnlyRemainingFields={true}
                  />

                  {(mode === 'create' || mode === 'edit') && canShowPreview && (
                    <div className="mt-4">
                      <button
                        type="button"
                        onClick={() => setOpenPreview(true)}
                        className="w-full btn btn-primary flex items-center justify-center gap-2 py-3 px-4 text-sm font-medium transition-all duration-200 hover:shadow-md focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 m-2"
                        data-testid="preview-button"
                      >
                        <KeenIcon
                          icon="eye"
                          className="text-white"
                          style="filled"
                        />
                        <KeenIcon
                          icon="globe"
                          className="text-white"
                          style="filled"
                        />
                        <span>
                          {intl.formatMessage({
                            id: 'ORGANIZATION.BUTTON.PREVIEW_TRANSLATIONS',
                          })}
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {(mode === 'create' || mode === 'edit') && (
                <div className="mt-6 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => onClose?.({}, 'backdropClick')}
                    className="btn btn-sm btn-light"
                    disabled={isSubmitting}
                    data-testid="cancel-button"
                  >
                    {intl.formatMessage({ id: 'ORGANIZATION.BUTTON.CANCEL' })}
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="btn btn-sm btn-primary"
                    disabled={isSubmitting}
                    data-testid="submit-button"
                  >
                    {isSubmitting
                      ? intl.formatMessage({ id: 'ORGANIZATION.BUTTON.SAVING' })
                      : mode === 'create'
                        ? intl.formatMessage({
                            id: 'ORGANIZATION.BUTTON.CREATE',
                          })
                        : intl.formatMessage({
                            id: 'ORGANIZATION.BUTTON.SAVE',
                          })}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </Modal>

      {(mode === 'create' || mode === 'edit') && (
        <OrganizationPreviewModal
          open={openPreview}
          onClose={() => setOpenPreview(false)}
          formData={formData}
          source_language={formData.language || currentLanguage.code}
          onTranslationsGenerated={handleTranslationsGenerated}
        />
      )}
    </>
  );
};

export { OrganizationFormModal };
